import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  input,
  model,
  OnInit,
  output,
  viewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { ButtonModule } from '@openng/optimus-ui/button';
import { FilterFormStateService } from '@shared/modules/page-settings';
import {
  FormComponent,
  PROJECTED_FORM_HOST,
  ProjectedFormGroupDirective,
  ProjectedFormHost,
  projectedFormProxy,
} from '@shared/ui/forms';
import { scrollToFirstErrorInput } from '@shared/utils';
import { ButtonsComponent } from '../buttons/buttons.component';

/** data-cy atributy prvků filtru (hodnoty z `Cy_*` konstant přehledu). */
export interface BrowseFilterCy {
  form?: string;
  /** panel akordeonu v režimu `collapsibleLabel` */
  panel?: string;
  search?: string;
  clear?: string;
}

/** Po úspěšném odeslání zavřít sekci akordeonu a pole vymazat (jednorázový údaj, např. důvod vyhledávání). */
export interface BrowseFilterClearAfterSubmit {
  control: string;
  section?: number;
}

/** Hodnota se maže až po zavření sekce akordeonu (animace), jinak by prázdné pole při zavírání problikne. */
const CLEAR_AFTER_SUBMIT_DELAY_MS = 600;

/**
 * Filtr přehledu: formulář (`ui-form`) s projektovanými poli, tlačítka Vyhledat / Smazat kritéria a uložení
 * stavu filtru (hodnoty + otevřené sekce akordeonu) pod `pageKey`, aby se po návratu z detailu obnovil.
 *
 * Odeslání: platný formulář → `submitted` a uložení stavu; neplatný → posun na první chybné pole a `invalidSubmit`
 * (přehled si otevře sekci s chybou). Smazání: reset formuláře, smazání uloženého stavu, `cleared`.
 *
 * `collapsibleLabel` zabalí pole i tlačítka do jediného panelu akordeonu (veřejné katalogy); panel se po
 * obnovení stavu otevře, když je ve filtru nějaká hodnota.
 *
 * Pole jsou projektovaný obsah vnitřního `ui-form` – formulář a stav odeslání jim zprostředkuje hostitel
 * (`ProjectedFormGroupDirective`, `projectedFormProxy`, viz libs/shared/ui/forms/src/lib/form/projected-form.ts).
 */
@Component({
  selector: 'ui-browse-filter',
  templateUrl: './browse-filter.component.html',
  imports: [NgTemplateOutlet, FormComponent, ButtonsComponent, ButtonModule, AccordionModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [{ directive: ProjectedFormGroupDirective, inputs: ['formGroup: form'] }],
  providers: [
    { provide: PROJECTED_FORM_HOST, useExisting: forwardRef(() => BrowseFilterComponent) },
    { provide: FormComponent, useFactory: projectedFormProxy },
  ],
})
export class BrowseFilterComponent implements OnInit, ProjectedFormHost {
  private readonly filterFormState = inject(FilterFormStateService);
  private readonly formGroupDirective = inject(ProjectedFormGroupDirective, { self: true });

  /** Klíč uloženého stavu filtru; bez něj se stav neukládá. */
  readonly pageKey = input<string>();
  /** Otevřené sekce akordeonu – sdílené s komponentou polí a ukládané se stavem. */
  readonly accordion = model<number[]>([]);
  readonly collapsibleLabel = input<string>();
  readonly submitLabel = input('Vyhledat');
  readonly clearLabel = input('Smazat kritéria');
  readonly cy = input<BrowseFilterCy>();
  /** Vlastní podmínka platnosti místo `form.valid` (např. archiv bez povinného důvodu). */
  readonly validate = input<() => boolean>();
  readonly clearAfterSubmit = input<BrowseFilterClearAfterSubmit>();

  /** Vnitřní `ui-form` – komponenty polí z něj čtou stav odeslání (`submitted`) pro zobrazení chyb; k dispozici
   * až po vytvoření pohledu (v šabloně přehledu `@if (filtr.formComponent(); as uiForm)`). */
  readonly formComponent = viewChild(FormComponent);

  readonly submitted = output<void>();
  readonly cleared = output<void>();
  readonly invalidSubmit = output<void>();

  /** Formulář filtru (vstup `form` – binding host direktivy `FormGroupDirective`). */
  get form(): FormGroup {
    return this.formGroupDirective.form;
  }

  ngOnInit(): void {
    this.restoreState();
  }

  submit(): void {
    const valid = this.validate()?.() ?? this.form.valid;
    if (!valid) {
      this.invalidSubmit.emit();
      scrollToFirstErrorInput(this.form);
      return;
    }

    this.submitted.emit();
    const clear = this.clearAfterSubmit();
    if (!clear) {
      this.saveState();
      return;
    }
    if (clear.section !== undefined) {
      this.accordion.update((sekce) => sekce.filter((value) => value !== clear.section));
    }
    setTimeout(() => {
      this.form.get(clear.control)?.setValue(null);
      this.saveState();
    }, CLEAR_AFTER_SUBMIT_DELAY_MS);
  }

  clear(): void {
    this.form.reset();
    this.clearState();
    this.cleared.emit();
  }

  /** Uloží hodnoty a otevřené sekce (volat i před odchodem na detail, pokud se mezitím změnily). */
  saveState(): void {
    const pageKey = this.pageKey();
    if (!pageKey) return;
    this.filterFormState.setFormState(pageKey, {
      values: this.form.value,
      uiState: { accordionIndices: this.accordion() },
    });
  }

  clearState(): void {
    const pageKey = this.pageKey();
    if (pageKey) this.filterFormState.clearFormState(pageKey);
  }

  protected onAccordionChange(value: unknown): void {
    this.accordion.set(Array.isArray(value) ? (value as number[]) : value == null ? [] : [value as number]);
  }

  private restoreState(): void {
    const pageKey = this.pageKey();
    const saved = pageKey ? this.filterFormState.getFormState(pageKey) : null;
    if (!saved) return;

    this.form.patchValue(saved.values);
    const indices = saved.uiState?.accordionIndices;
    if (Array.isArray(indices)) {
      this.accordion.set(indices);
    } else if (this.collapsibleLabel() && hasAnyValue(saved.values)) {
      this.accordion.set([0]);
    }
  }
}

/** Je ve filtru (včetně vnořených skupin, např. rozsahů dat) aspoň jedna hodnota? */
function hasAnyValue(values: unknown): boolean {
  if (values == null || values === '') return false;
  if (typeof values !== 'object') return true;
  return Object.values(values as Record<string, unknown>).some((value) => hasAnyValue(value));
}
